import useOnClickOutside from "@/hooks/useOnClickOutside";
import { FC, useRef, useState } from "react";
import styled, { css } from "styled-components";

const Holder = styled.div`
  position: relative;
`;

const Button = styled.button<{ $isDropdownOpen: boolean; $isBlack?: boolean }>`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  display: flex;
  align-items: center;
  height: 32px;
  padding: 0 34px 0 12px;
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.gray};
  font-family: "Manrope", sans-serif;
  font-weight: 700;
  font-size: 11px;
  line-height: 15px;
  transition: all ease-out 0.2s;
  svg {
    position: absolute;
    top: calc(50% - 7px);
    right: 12px;
    transition: all ease-out 0.2s;
  }
  ${({ $isDropdownOpen }) =>
    $isDropdownOpen &&
    css`
      svg {
        transform: rotate(180deg);
      }
    `}
  ${({ $isBlack }) =>
    $isBlack &&
    css`
      color: ${({ theme }) => theme.colors.black};
    `}
  &:hover {
    color: ${({ theme }) => theme.colors.black};
  }
`;

const Dropdow = styled.div`
  width: 80px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  padding: 4px;
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  position: absolute;
  right: 0;
  top: calc(100% + 10px);
  z-index: 2;
`;

const DropdownButton = styled.button`
  width: 100%;
  color: ${({ theme }) => theme.colors.gray};
  font-family: "Manrope", sans-serif;
  font-weight: 700;
  font-size: 11px;
  line-height: 15px;
  display: flex;
  height: 40px;
  padding: 0 12px;
  align-items: center;
  transition: all ease-out 0.2s;
  border-radius: 4px;
  &:hover {
    color: ${({ theme }) => theme.colors.black};
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
  }
`;

const Break = styled.div`
  background-color: ${({ theme }) => theme.colors.grayscale};
  height: 1px;
  margin: 8px;
`;

interface Props {
  sequenceValue: {
    value: number | "All";
    type: string;
  };
  setSequenceValue: React.Dispatch<
    React.SetStateAction<{
      value: number | "All";
      type: string;
    }>
  >;
}

const ParticipantsScoreSequenceFilter: FC<Props> = ({
  sequenceValue,
  setSequenceValue,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const componentRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(componentRef, () => setShowDropdown(false));

  return (
    <Holder ref={componentRef}>
      <Button
        $isBlack={sequenceValue.value !== "All"}
        onClick={() => {
          setShowDropdown((prev) => !prev);
        }}
        $isDropdownOpen={showDropdown}
      >
        Test Sequence · {sequenceValue.type}
        {sequenceValue.value}
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.33203 5.25L6.9987 9.91667L11.6654 5.25"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Button>
      {!!showDropdown && (
        <Dropdow>
          <DropdownButton
            onClick={() => {
              setShowDropdown(false);
              setSequenceValue({
                value: "All",
                type: "",
              });
            }}
          >
            All
          </DropdownButton>
          {Array.from(Array(10).keys()).map((_, index) => (
            <DropdownButton
              key={index}
              onClick={() => {
                setShowDropdown(false);
                setSequenceValue({
                  value: index + 1,
                  type: "T",
                });
              }}
            >
              T{index + 1}
            </DropdownButton>
          ))}
          <Break />
          {Array.from(Array(2).keys()).map((_, index) => (
            <DropdownButton
              key={index}
              onClick={() => {
                setShowDropdown(false);
                setSequenceValue({
                  value: index + 1,
                  type: "C",
                });
              }}
            >
              C{index + 1}
            </DropdownButton>
          ))}
        </Dropdow>
      )}
    </Holder>
  );
};

export default ParticipantsScoreSequenceFilter;
